// The trend chart's template, chart.trend (contract §8.3), and the history
// drawing it shares with the chart instrument: HistoryChart
// (cloud/web/src/components/charts/HistoryChart.tsx) as a fitted svg (§4.5.2)
// at the board's height. Its margins are HistoryChart's (18 12 22 42), its
// value and time ticks and its day bands are the host's (§4.6), so they fall
// where the app's chart puts them, and the series are drawn by stroke as the
// v2 chart rules draw them: the chart line solid, the second dashed ink, the
// third dotted grey. A single series has its min/max band and its "now" dot,
// and its low and high marks; several share one value scale (one group).
//
// Several series have their legend over the plot, each its name and a key in
// its own stroke, as HistoryChart draws one for two or more. An angle reading
// takes its own scale, its range (§4.5.4: [0, 360], or [−180, 180] for a
// relative angle), in a group of its own, since a group never mixes a range
// with an automatic scale; the chart's value ticks are its first series'.
//
// Where the rendered check (§9.6) asks more than today's chart: the low and
// high labels are values, so they are 14 px rather than the chart's 11 px.
//
// A chart is a record (Mike, 2026-10-14, TOM-665): its lines draw what the
// window holds whatever the reading now, and a window that holds nothing
// keeps the chart's frame, its time axis, a plain grid and a small note,
// "No data in this window", never a collapsed card.
//
// The Marina look (cloud ADR 0396, TOM-678): the chart line, its "now" dot
// and, on a single series, the area under the line take the first reading's
// kind (`data-domain`); the area fades from the kind's top opacity to its
// foot, over the min/max band, which stays the neutral track so it never
// reads as a second series. The second and third series stay dashed ink and
// dotted grey, so a chart never reads two kinds' colours as one scale.
//
// Two units (TOM-953, board pages v2 only): the first reading's unit keeps
// the left axis, and readings of one other unit go on a right axis, group
// `y2`, keyed by the path's store unit as the chart widget keys its panels.
// A reading of a third unit is not drawn; the server refuses one. A right
// line is drawn in its kind's colour and keeps the dash of its place (the
// second series dashed, the third dotted), so the night theme, where every
// kind is red, still tells the lines apart. The right numbers are in that
// colour, in a right margin as wide as the left, with no grid lines of
// their own: the host aligns their scale so they sit on the left axis's
// grid lines (§4.6). Each axis has its unit over it, and the legend names
// every reading with its unit, the right ones "right axis", since the svg
// is hidden from a screen reader. With a chip, a small line over the legend
// gives the first right reading's same statistic after its key; the chip
// itself stays the first reading's. A chart of one unit is written exactly
// as before.

import {
  AREA_CSS,
  angleRange,
  areaGradient,
  css,
  domainCss,
  domainOf,
  domainsOf,
  h,
  output,
  pathFacts,
  readingNames,
  reading,
} from "./kit.js";
import { pageDomainFor } from "./domains.js";

const MARGIN = { top: 18, right: 12, bottom: 22, left: 42 };
/** A chart with a right axis: its right margin as wide as the left (TOM-953). */
const MARGIN_RIGHT_AXIS = { ...MARGIN, right: MARGIN.left };
/** The statistics a chip may name (the protocol's NEO_CHART_CHIPS). */
const CHIP_STATS = ["last", "min", "max", "mean", "change"];
/** HistoryChart's value ticks (linearTicks to about 4, spaced 16 px): at most this many. */
const Y_TICKS = 7;
/** Time ticks the host writes: k is 0 to 7. */
const X_TICKS = 8;
/** Local days a window of 40 hours to 366 days touches, at most (k is 0 to 13). */
const DAY_BANDS = 14;
const HOUR_MS = 3_600_000;

/** A window string's span in ms, when it is a rolling or fixed window. */
function windowSpanMs(window) {
  const rolling = /^([1-9][0-9]{0,5})(m|h|d)$/.exec(window);
  if (rolling !== null)
    return (
      Number(rolling[1]) * { m: 60_000, h: HOUR_MS, d: 86_400_000 }[rolling[2]]
    );
  const [from, to] = String(window).split("/");
  const span = Date.parse(to) - Date.parse(from);
  return Number.isFinite(span) ? span : 0;
}

/**
 * The CSS of the history drawings with class `p` (short class names keep a
 * page small), in a plot inset by `M` from the fitted svg's box. With
 * `rightAxis`, the right axis's numbers, the units over both axes, a right
 * line's and its key's kind colour and the second value's line (TOM-953).
 */
export function historyCss(p, M = MARGIN, rightAxis = false) {
  // The plot's width and height, named once (TOM-1001): the expressions ran
  // about fifteen times and kept the chart widget at its byte budget.
  const pw = "var(--_pw)";
  const ph = "var(--_ph)";
  const x = (frac) => `calc(${M.left}px + ${frac} * ${pw} * 1px)`;
  const y = (frac) => `calc(${M.top}px + (1 - ${frac}) * ${ph} * 1px`;
  return (
    css(`
    .${p} { display: block; width: 100%; overflow: visible; font-family: var(--tc-font-sans);
      --_pw: calc(var(--tc-w,320) - ${M.left + M.right}); --_ph: calc(var(--tc-h,168) - ${M.top + M.bottom}); }
    .${p} text { font-variant-numeric: tabular-nums; font-size: 11px; fill: var(--muted); }
    .${p} .t { text-anchor: end; transform: translate(${M.left - 8}px, ${y("var(--frac,0)")} + 3.5px)); }
    .${p} .g, .${p} .b { stroke: var(--grid); stroke-width: 0.5; vector-effect: non-scaling-stroke;
      transform: translate(${M.left}px, ${y("var(--frac,0)")})) scaleX(calc(${pw})); }
    .${p} .b { stroke-width: 1; transform: translate(${M.left}px, calc(var(--tc-h,168) * 1px - ${M.bottom}px)) scaleX(calc(${pw})); }
    .${p} .x { text-anchor: middle; transform: translate(${x("var(--frac,0)")}, calc(var(--tc-h,168) * 1px - ${M.bottom - 15}px)); }
    .${p} .d { fill: var(--text); opacity: 0.04;
      transform: translate(${x("var(--x0,0)")}, ${M.top}px) scale(calc((var(--x1,0) - var(--x0,0)) * ${pw}), calc(${ph})); }
    .${p} .dl { font-weight: 600; text-anchor: middle;
      transform: translate(${x("(var(--x0,0) + var(--x1,0)) / 2")}, ${M.top + 12}px); }
    .${p} .bd { fill: var(--track); }
    .${p} .ar { stroke: none; }
    .${p} .l { fill: none; stroke: var(--_c); stroke-width: 2.4; stroke-linecap: round; stroke-linejoin: round; }
    .${p} .l2 { stroke: var(--tc-series-2); stroke-width: 2.5; stroke-dasharray: 10 4; stroke-linecap: butt; }
    .${p} .l3 { stroke: var(--tc-series-3); stroke-width: 2; stroke-dasharray: 1 5; }
    .${p} .n { fill: var(--_c); stroke: var(--background); stroke-width: 2;
      transform: translate(${x("var(--x,0)")}, ${y("var(--y,0)")})); }
    .${p} .m { font-size: 14px; font-weight: 600; fill: var(--text); text-anchor: middle; }
    .${p} .hi { transform: translate(${x("var(--x,0)")}, ${y("var(--y,0)")} - 8px)); }
    .${p} .lo { transform: translate(${x("var(--x,0)")}, ${y("var(--y,0)")} + 18px)); }
    .hc-lg { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 14px; flex: none;
      font-size: 11.5px; color: var(--muted); padding: 0 2px; }
    .hc-lg > span { display: inline-flex; align-items: center; gap: 6px; white-space: nowrap; }
    .hc-k { width: 22px; height: 2.4px; flex: none; background: var(--_k,var(--_c)); }
    .hc-k2, .hc-k3 { background: repeating-linear-gradient(90deg,var(--_k) 0 var(--_d),transparent 0 var(--_g)); }
    .hc-k2 { --_k: var(--tc-series-2); --_d: 10px; --_g: 14px; height: 2.5px; }
    .hc-k3 { --_k: var(--tc-series-3); --_d: 2px; --_g: 6px; height: 2px; }
    .${p} .e { display: none; }
    .${p} .l[d=""] ~ .e { display: inline; }
    .${p} .l:not([d=""]) ~ .e, .${p} .l[d=""] ~ .m { display: none; }
    .${p} .e rect, .${p} .e line { fill: none; stroke: var(--grid); vector-effect: non-scaling-stroke;
      transform: translate(${M.left}px, ${M.top}px) scale(calc(${pw}), calc(${ph})); }
    .${p} .e line { transform: translate(${M.left}px, ${y("0.5")})) scaleX(calc(${pw})); }
    .${p} .e text { font-size: 13px; text-anchor: middle; transform: translate(${x("0.5")}, ${y("0.5")} - 8px)); }
    .${p} .n[data-quality=missing], .${p} [data-tc-tick][data-quality=missing],
      .${p} [data-tc-time-tick][data-quality=missing], .${p} [data-tc-day-band][data-quality=missing] { display: none; }
    @media (min-width: ${640 + M.left + M.right}px) {
      .${p} .l { stroke-width: 2.8; }
      .${p} .l2 { stroke-width: 3; }
      .${p} .l3 { stroke-width: 2.5; }
      .${p} .n { stroke-width: 1.556;
        transform: translate(${x("var(--x,0)")}, ${y("var(--y,0)")})) scale(1.2857); }
    }
  `) + (rightAxis ? rightAxisCss(p, M) : "")
  );
}

/** The right axis's CSS (TOM-953): only a chart that has one carries it. */
function rightAxisCss(p, M) {
  const right = `calc(var(--tc-w,320) * 1px - ${M.right - 8}px)`;
  return css(`
    .${p} .t2 { text-anchor: start; transform: translate(${right},
      calc(${M.top}px + (1 - var(--frac,0)) * (var(--tc-h,168) - ${M.top + M.bottom}) * 1px + 3.5px)); }
    .${p} .u { font-size: 10px; text-anchor: end; transform: translate(${M.left - 8}px, 8px); }
    .${p} .u2 { text-anchor: start; transform: translate(${right}, 8px); }
    .${p} .ra[data-domain] text, .${p} .n2[data-domain] { fill: var(--_c); }
    .${p} .n2 { fill: var(--tc-series-2); }
    .${p} .l.r { stroke: var(--_c); }
    .hc-k.r { --_k: var(--_c); }
  `);
}

/**
 * One history drawing: a fitted svg of class `p` whose series are `names`
 * (reading names, the first the chart line), over `window` at `points`, on
 * one value scale `group`, in a plot inset by `M`. `angle` names the
 * readings that are angles: they draw a line only (§4.5.4). `right` maps the
 * readings on a right axis, group `${group}2`, to their kind (TOM-953): they
 * draw in its colour, under that axis's numbers and unit.
 */
export function historyChart({
  p,
  fit,
  group,
  names,
  window,
  points,
  angle = new Set(),
  right = new Map(),
  M = MARGIN,
}) {
  const single = names.length === 1;
  const span = windowSpanMs(window);
  // An angle's scale is its range: a group of its own (§4.5.3).
  const groupOf = (name) =>
    angle.has(name) ? `${group}-a` : right.has(name) ? `${group}2` : group;
  const own = (name) => ({
    "data-tc-points": String(points),
    "data-tc-group": groupOf(name),
    "data-tc-inset": `${M.top} ${M.right} ${M.bottom} ${M.left}`,
  });
  const ticks = names[0] === undefined ? group : groupOf(names[0]);
  const parts = [];
  // Day bands under everything, for windows of 40 hours and more: the
  // alternate days shaded, and on a window of at most 48 hours each day's
  // label at the top.
  if (span >= 40 * HOUR_MS) {
    for (let k = 1; k < DAY_BANDS; k += 2)
      parts.push(
        h("rect", { class: "d", width: 1, height: 1, "data-tc-day-band": k }),
      );
    if (span <= 48 * HOUR_MS)
      for (let k = 0; k < 3; k += 1)
        parts.push(
          h("text", {
            class: "dl",
            "data-tc-day-band": k,
            "data-tc-field": "label",
          }),
        );
  }
  for (let k = 0; k < Y_TICKS; k += 1)
    parts.push(
      h("line", {
        class: "g",
        x2: 1,
        "data-tc-scale": ticks,
        "data-tc-tick": k,
      }),
      h("text", { class: "t", "data-tc-scale": ticks, "data-tc-tick": k }),
    );
  // The right axis (TOM-953): its numbers, which the host aligns to the left
  // axis's grid lines (§4.6), and the units over both axes.
  const [firstRight, rightDomain] = [...right][0] ?? [];
  if (firstRight !== undefined) {
    const labels = [];
    for (let k = 0; k < Y_TICKS; k += 1)
      labels.push(
        h("text", {
          class: "t2",
          "data-tc-scale": `${group}2`,
          "data-tc-tick": k,
        }),
      );
    parts.push(
      h("text", { class: "u", "data-tc-unit": names[0] }),
      h(
        "g",
        { class: "ra", ...domainOf(rightDomain) },
        labels,
        h("text", { class: "u u2", "data-tc-unit": firstRight }),
      ),
    );
  }
  parts.push(h("line", { class: "b", x2: 1 }));
  for (let k = 0; k < X_TICKS; k += 1)
    parts.push(h("text", { class: "x", "data-tc-time-tick": k }));
  names.forEach((name, i) => {
    if (single && !angle.has(name))
      parts.push(
        h("path", {
          class: "bd",
          "data-tc-path": name,
          "data-tc-shape": "band",
          ...own(name),
        }),
        h("path", {
          class: "ar",
          "data-tc-path": name,
          "data-tc-shape": "area",
          ...own(name),
          fill: `url(#${fit}-a)`,
        }),
      );
    // A right line takes its kind's colour and keeps its place's dash.
    const kind = right.get(name);
    parts.push(
      h("path", {
        class: `${i === 0 ? "l" : `l l${Math.min(3, i + 1)}`}${typeof kind === "string" ? " r" : ""}`,
        "data-tc-path": name,
        ...own(name),
        ...domainOf(kind),
      }),
    );
  });
  const first = names[0];
  if (first !== undefined) {
    if (single)
      for (const at of ["max", "min"])
        parts.push(
          h("text", {
            class: `m ${at === "max" ? "hi" : "lo"}`,
            "data-tc-mark": first,
            "data-tc-at": at,
            "data-tc-text": first,
            "data-tc-stat": at,
            ...own(first),
          }),
        );
    parts.push(
      h("circle", {
        class: "n",
        r: 7,
        "data-tc-mark": first,
        "data-tc-at": "last",
        ...own(first),
      }),
    );
  }
  if (firstRight !== undefined)
    parts.push(
      h("circle", {
        class: "n n2",
        r: 6,
        "data-tc-mark": firstRight,
        "data-tc-at": "last",
        ...own(firstRight),
        ...domainOf(rightDomain),
      }),
    );
  // An empty window's frame, a grid line and its note, after every line so
  // the lines decide whether they show (TOM-665).
  parts.push(
    h(
      "g",
      { class: "e" },
      h("rect", { width: 1, height: 1 }),
      h("line", { x2: 1 }),
      h("text", {}, "No data in this window"),
    ),
  );
  return h(
    "svg",
    {
      class: p,
      "data-tc-fit": fit,
      "data-tc-window": window,
      "aria-hidden": "true",
    },
    single && first !== undefined && !angle.has(first)
      ? h("defs", {}, areaGradient(`${fit}-a`))
      : "",
    parts,
  );
}

/**
 * A chart's legend for several series (HistoryChart's, for two or more): each
 * series' key in its own stroke, then its name, `label(name, i)` or the
 * reading's own (`data-tc-name`). With a right axis (`right`, as
 * historyChart takes it, TOM-953) each is the reading's own name followed
 * by its unit, the right ones' by "right axis" too, in one span so they read
 * as one phrase, and a right key takes its kind's colour.
 */
export function historyLegend(names, label = null, right = new Map()) {
  if (names.length < 2) return "";
  const units = right.size > 0;
  return h(
    "div",
    { class: "hc-lg" },
    names.map((name, i) => {
      const kind = right.get(name);
      return h(
        "span",
        {},
        h("i", {
          class: `hc-k hc-k${Math.min(3, i + 1)}${typeof kind === "string" ? " r" : ""}`,
          ...domainOf(kind),
        }),
        units
          ? h(
              "span",
              {},
              h("span", { "data-tc-name": name }),
              " · ",
              h("span", { "data-tc-unit": name }),
              right.has(name) ? ", right axis" : "",
            )
          : label === null
            ? h("span", { "data-tc-name": name })
            : label(name, i),
      );
    }),
  );
}

/**
 * The window a chart's source draws over (contract §11.2): its rolling span,
 * else its fixed window, else `fallback`.
 */
export function chartWindow(spec, rolling, fallback = "24h") {
  if (typeof rolling === "number" && rolling > 0) return rollingWindow(rolling);
  const w = spec?.window;
  if (w && typeof w.from === "string" && typeof w.to === "string") {
    const from = Date.parse(w.from);
    const to = Date.parse(w.to);
    const span = to - from;
    if (Number.isFinite(span) && span >= 300_000 && span <= 366 * 86_400_000)
      return `${new Date(from).toISOString()}/${new Date(to).toISOString()}`;
  }
  return fallback;
}

/**
 * The canonical rolling window for a span in seconds: the protocol's
 * neoPageWindowFromRolling, whole days, else whole hours, else whole
 * minutes, kept inside 5 minutes to 366 days.
 */
export function rollingWindow(seconds) {
  if (Number.isNaN(seconds)) return "5m";
  const ms = Math.min(
    366 * 86_400_000,
    Math.max(300_000, Math.round(seconds) * 1000),
  );
  if (ms % 86_400_000 === 0) return `${ms / 86_400_000}d`;
  if (ms % HOUR_MS === 0) return `${ms / HOUR_MS}h`;
  return `${Math.max(5, Math.round(ms / 60_000))}m`;
}

/** chart.trend: a trend chart of 1 to 3 paths in up to two units (§8.3). */
export function chartTrend(source, context) {
  const spec = source.spec;
  const all = (Array.isArray(spec.paths) ? spec.paths : []).slice(0, 3);
  // Each reading's axis by its store unit (TOM-953): the first unit's on the
  // left, the next one's on the right, a third's not drawn. An angle is on
  // its own range and counts as no unit. So does a path whose unit the
  // server does not know (not in the boat's catalog when the page is made):
  // it stays on the left, as version 3 drew every path, so a chart of one
  // unit never grows a right axis from a missing fact.
  const unitOf = (path) => {
    const unit = pathFacts(context, path).storeUnit;
    return typeof unit === "string" && unit !== "" ? unit.toLowerCase() : null;
  };
  const units = [];
  const paths = all.filter((path) => {
    const unit = unitOf(path);
    if (pathFacts(context, path).angle || unit === null) return true;
    if (!units.includes(unit)) units.push(unit);
    return units.indexOf(unit) < 2;
  });
  const names = readingNames(paths);
  const readings = {};
  const angle = new Set();
  const right = new Map();
  for (const [path, name] of names) {
    const facts = pathFacts(context, path);
    readings[name] = facts.angle
      ? reading(path, angleRange(path, facts.storeUnit))
      : reading(path);
    if (facts.angle) angle.add(name);
    else if (units.length > 1 && unitOf(path) === units[1])
      right.set(name, pageDomainFor(path));
  }
  const window = chartWindow(spec, source.rolling);
  const list = [...names.values()];
  const chip =
    typeof spec.chip === "string" && list[0] !== undefined
      ? { reading: list[0], stat: spec.chip }
      : null;
  // With a chip, the first right reading's same statistic: the card draws it
  // under the chip, right-aligned in the header (Mike, 2026-10-31, TOM-953),
  // read at the chip's 192 points (§4.7). A reader that does not know
  // `secondChip` draws the chip alone; the legend still names each axis.
  const [firstRight] = [...right][0] ?? [];
  const secondChip =
    chip !== null && firstRight !== undefined && CHIP_STATS.includes(chip.stat)
      ? { reading: firstRight, stat: chip.stat }
      : null;
  const html = h(
    "div",
    { class: "tp tr", ...domainOf(pageDomainFor(paths[0])) },
    historyLegend(list, null, right),
    historyChart({
      p: "hc",
      fit: "plot",
      group: "y",
      names: list,
      window,
      points: 96,
      angle,
      right,
      M: right.size > 0 ? MARGIN_RIGHT_AXIS : MARGIN,
    }),
  );
  return output("chart.trend", 1, {
    html,
    css:
      css(
        `.tr { justify-content: flex-start; gap: 4px; } .tr .hc { height: 168px; flex: none; }`,
      ) +
      historyCss(
        "hc",
        right.size > 0 ? MARGIN_RIGHT_AXIS : MARGIN,
        right.size > 0,
      ) +
      AREA_CSS +
      domainCss(domainsOf(paths)),
    readings,
    card: {
      kind: "chart",
      window: true,
      chip,
      ...(secondChip === null ? {} : { secondChip }),
    },
  });
}
